import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth, postAuthDestination } from "@/contexts/AuthContext";
import { CallProvider } from "@/contexts/CallContext";
import PersistentCallHost from "@/components/PersistentCallHost";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";
import GoogleOAuthCallback from "@/components/GoogleOAuthCallback";
import GoogleOneTap from "@/components/GoogleOneTap";
import DashboardLayout from "@/components/DashboardLayout";
import WhatsAppWidget from "@/components/WhatsAppWidget";
import RouteSEO from "@/components/RouteSEO";
import Preloader from "@/components/Preloader";
import ScrollToTop from "@/components/ScrollToTop";
import { setMediaOverrides } from "@/lib/brandAssets";
import api from "@/lib/api";

import Landing from "@/pages/Landing";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import PublishedPapers from "@/pages/PublishedPapers";
import PaperDetail from "@/pages/PaperDetail";
import ResearchIdeasPublic from "@/pages/ResearchIdeasPublic";
import ExplainersPublic from "@/pages/ExplainersPublic";
import DiscussionsPublic from "@/pages/DiscussionsPublic";
import DiscussionThread from "@/pages/DiscussionThread";
import DatasetsPublic from "@/pages/DatasetsPublic";

// V3 onboarding
import OnboardingRole from "@/pages/OnboardingRole";
import OnboardingAuth from "@/pages/OnboardingAuth";
import OnboardingProfile from "@/pages/OnboardingProfile";
import AccountResetPassword from "@/pages/AccountResetPassword";
import AccountSpecialization from "@/pages/AccountSpecialization";
import EmployeeDirectReports from "@/pages/EmployeeDirectReports";
import EmployeeDirectReportDetail from "@/pages/EmployeeDirectReportDetail";
import AdminOrgUnits from "@/pages/AdminOrgUnits";
import MentorPublicProfile from "@/pages/MentorPublicProfile";
import AccountPendingVerification from "@/pages/AccountPendingVerification";

// V3 payments
import PaymentCheckout from "@/pages/PaymentCheckout";
import PaymentReturn from "@/pages/PaymentReturn";

import Dashboard from "@/pages/Dashboard";
import Ideas from "@/pages/Ideas";
import IdeaNew from "@/pages/IdeaNew";
import IdeaDetail from "@/pages/IdeaDetail";
import Mentors from "@/pages/Mentors";
import Journals from "@/pages/Journals";
import Courses from "@/pages/Courses";
import CourseDetail from "@/pages/CourseDetail";
import CourseEditor from "@/pages/CourseEditor";
import CourseLearn from "@/pages/CourseLearn";
import Certificate from "@/pages/Certificate";
import Surveys from "@/pages/Surveys";
import SurveyAnalytics from "@/pages/SurveyAnalytics";
import SurveyDetail from "@/pages/SurveyDetail";
import Portfolio from "@/pages/Portfolio";
import Plagiarism from "@/pages/Plagiarism";
import AnnouncementsPage from "@/pages/AnnouncementsPage";

// V3 client/student pages
import GuidanceRequests from "@/pages/GuidanceRequests";
import GuidanceNew from "@/pages/GuidanceNew";
import GuidanceWorkspace from "@/pages/GuidanceWorkspace";
import TaskWorkspace from "@/pages/TaskWorkspace";
import StudentProjectUpload from "@/pages/StudentProjectUpload";

import MentorDashboard from "@/pages/MentorDashboard";
import MentorRequestsPage from "@/pages/MentorRequestsPage";
import MentorProjectsPage from "@/pages/MentorProjectsPage";
import AuthorEarnings from "@/pages/AuthorEarnings";
import GetHelp from "@/pages/GetHelp";

import InstitutionDashboard from "@/pages/InstitutionDashboard";
import InstitutionMembersPage from "@/pages/InstitutionMembersPage";

import AdminDashboard from "@/pages/AdminDashboard";
import AdminUsersPage from "@/pages/AdminUsersPage";
import AdminMentorRequestsPage from "@/pages/AdminMentorRequestsPage";
import AdminPapersPage from "@/pages/AdminPapersPage";
import AdminProjectsPage from "@/pages/AdminProjectsPage";
import AdminAnalyticsPage from "@/pages/AdminAnalyticsPage";
import AdminConfigPage from "@/pages/AdminConfigPage";
import AdminNavVisibility from "@/pages/AdminNavVisibility";
// V3 admin pages
import AdminPendingIdeasPage from "@/pages/AdminPendingIdeasPage";
import AdminGuidancePage from "@/pages/AdminGuidancePage";
import Careers from "@/pages/Careers";
import AdminVacancies from "@/pages/AdminVacancies";
import AdminTeams from "@/pages/AdminTeams";
import AdminTasks from "@/pages/AdminTasks";
import VerifyEmail from "@/pages/VerifyEmail";
import InternDailyLog from "@/pages/InternDailyLog";
import AdminInternAnalytics from "@/pages/AdminInternAnalytics";
import EmployeeKanban from "@/pages/EmployeeKanban";
import EmployeeMyTeams from "@/pages/EmployeeMyTeams";
import EmployeeSubmissions from "@/pages/EmployeeSubmissions";
import EmployeeAssignedProjects from "@/pages/EmployeeAssignedProjects";
import { EmployeeAttendance, EmployeePayslips, EmployeeMeetings, ErpLeaves, ErpDocuments, ErpExpenses } from "@/pages/EmployeeErp";
import AdminPaymentsPage from "@/pages/AdminPaymentsPage";
import AdminPricingPage from "@/pages/AdminPricingPage";
import AdminTutorialsPage from "@/pages/AdminTutorialsPage";
import AdminRoleApprovalsPage from "@/pages/AdminRoleApprovalsPage";
import ChatPage from "@/pages/ChatPage";
import AdminEventsPage from "@/pages/AdminEventsPage";
import AdminCourseReviewPage from "@/pages/AdminCourseReviewPage";
import AdminJournalsPage from "@/pages/AdminJournalsPage";
import AdminTaxonomyPage from "@/pages/AdminTaxonomyPage";
import AdminResearchIdeasPage from "@/pages/AdminResearchIdeasPage";
import AdminDatasetsPage from "@/pages/AdminDatasetsPage";
import AdminExplainersPage from "@/pages/AdminExplainersPage";
import AdminPricingPlansPage from "@/pages/AdminPricingPlansPage";
import AdminEmailEnginePage from "@/pages/AdminEmailEnginePage";
import AdminMailboxPage from "@/pages/AdminMailboxPage";
import AdminMediaManagerPage from "@/pages/AdminMediaManagerPage";
import AdminCmsPage from "@/pages/AdminCmsPage";
import AdminLeadsPage from "@/pages/AdminLeadsPage";
import AdminTeamRolesPage from "@/pages/AdminTeamRolesPage";
import AdminBlogsPage from "@/pages/AdminBlogsPage";
import AdminLandingJourneyPage from "@/pages/AdminLandingJourneyPage";
import BlogsPublic from "@/pages/BlogsPublic";
import BlogDetail from "@/pages/BlogDetail";
import JournalsPublic from "@/pages/JournalsPublic";
import BrowseIdeasInDashboard from "@/pages/BrowseIdeasInDashboard";
import EventsPublic from "@/pages/EventsPublic";
import ResearchIdeaDetail from "@/pages/ResearchIdeaDetail";
import ExplainerDetail from "@/pages/ExplainerDetail";
import Pricing from "@/pages/Pricing";
import Privacy from "@/pages/Privacy";
import Team from "@/pages/Team";
import ProjectToPaper from "@/pages/ProjectToPaper";
import FAQ from "@/pages/FAQ";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import DmInboxFab from "@/components/DmInboxFab";

function RoleHome() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center kb-mono text-xs text-kb-ink-muted">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={postAuthDestination(user)} replace />;
}

function Router() {
  const location = useLocation();
  React.useEffect(() => {
    // Track page-view on every route change
    import("@/lib/tracker").then((m) => m.trackPageView());
  }, [location.pathname, location.search]);
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Hide WhatsApp widget on app dashboards & payment flows; show on all public pages
  const hideWhatsApp = /^\/(dashboard|mentor|admin|institution|onboarding|account|payments|app)(\b|\/)/.test(location.pathname);
  // Inverse: show the DM widget only inside the authenticated app (dashboard,
  // mentor, admin, institution shells) — not on public marketing pages.
  const showDmFab = /^\/(dashboard|mentor|admin|institution|account)(\b|\/)/.test(location.pathname);
  return (
    <>
      <RouteSEO />
      <GoogleOneTap />
      {!hideWhatsApp && <WhatsAppWidget />}
      {showDmFab && <DmInboxFab />}
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/about" element={<About />} />
      <Route path="/services" element={<Services />} />
      <Route path="/workshops" element={<Navigate to="/events" replace />} />
      <Route path="/events" element={<EventsPublic />} />
      <Route path="/publications" element={<PublishedPapers />} />
      <Route path="/publications/:id" element={<PaperDetail />} />
      <Route path="/contact" element={<Navigate to="/about#contact" replace />} />
      <Route path="/get-help" element={<GetHelp />} />
      <Route path="/get-involved" element={<Navigate to="/get-help" replace />} />
      <Route path="/research-ideas" element={<ResearchIdeasPublic />} />
      <Route path="/research-ideas/:id" element={<ResearchIdeaDetail />} />
      <Route path="/explainers" element={<ExplainersPublic />} />
      <Route path="/explainers/:id" element={<ExplainerDetail />} />
      <Route path="/discussions" element={<DiscussionsPublic />} />
      <Route path="/discussions/:id" element={<DiscussionThread />} />
      <Route path="/datasets" element={<DatasetsPublic />} />
      <Route path="/blogs" element={<BlogsPublic />} />
      <Route path="/blogs/:slug" element={<BlogDetail />} />
      <Route path="/journals" element={<JournalsPublic />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/team" element={<Team />} />
      <Route path="/careers" element={<Careers />} />
      <Route path="/mentors/:mentorId" element={<MentorPublicProfile />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/account/verify-email" element={<ProtectedRoute><VerifyEmail /></ProtectedRoute>} />
      <Route path="/project-to-paper" element={<ProjectToPaper />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/google" element={<GoogleOAuthCallback />} />
      <Route path="/survey/:id" element={<SurveyDetail />} />

      {/* V3 onboarding flow (public) */}
      <Route path="/onboarding/role" element={<OnboardingRole />} />
      <Route path="/onboarding/auth" element={<OnboardingAuth />} />
      <Route path="/onboarding/profile" element={<ProtectedRoute><OnboardingProfile /></ProtectedRoute>} />
      <Route path="/account/reset-password" element={<ProtectedRoute><AccountResetPassword /></ProtectedRoute>} />
      <Route path="/account/specialization" element={<ProtectedRoute><AccountSpecialization /></ProtectedRoute>} />
      <Route path="/account/pending-verification" element={<ProtectedRoute><AccountPendingVerification /></ProtectedRoute>} />

      {/* Post-auth router resolves correct dashboard */}
      <Route path="/app" element={<ProtectedRoute><RoleHome /></ProtectedRoute>} />

      {/* Payments */}
      <Route path="/payments/checkout" element={<ProtectedRoute><PaymentCheckout /></ProtectedRoute>} />
      <Route path="/payments/return" element={<PaymentReturn />} />

      {/* Client / Student dashboard */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="ideas" element={<Ideas />} />
        <Route path="ideas/browse" element={<BrowseIdeasInDashboard />} />
        <Route path="ideas/new" element={<IdeaNew />} />
        <Route path="ideas/:id" element={<IdeaDetail />} />
        <Route path="guidance" element={<GuidanceRequests />} />
        <Route path="intern/daily-log" element={<InternDailyLog />} />
        <Route path="employee/kanban" element={<EmployeeKanban />} />
        <Route path="announcements" element={<AnnouncementsPage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="employee/direct-reports" element={<EmployeeDirectReports />} />
        <Route path="employee/direct-reports/:uid" element={<EmployeeDirectReportDetail />} />
        <Route path="employee/teams" element={<EmployeeMyTeams />} />
        <Route path="assign-tasks" element={<AdminTasks />} />
        <Route path="employee/assigned-projects" element={<EmployeeAssignedProjects />} />
        <Route path="employee/submissions" element={<EmployeeSubmissions />} />
        <Route path="employee/attendance" element={<EmployeeAttendance />} />
        <Route path="employee/payslips" element={<EmployeePayslips />} />
        <Route path="employee/meetings" element={<EmployeeMeetings />} />
        <Route path="employee/erp/leaves" element={<ErpLeaves />} />
        <Route path="employee/erp/documents" element={<ErpDocuments />} />
        <Route path="employee/erp/expenses" element={<ErpExpenses />} />
        <Route path="employee/erp/performance" element={<ErpLeaves />} />
        <Route path="employee/erp/training" element={<ErpLeaves />} />
        <Route path="guidance/new" element={<GuidanceNew />} />
        <Route path="guidance/:id" element={<GuidanceWorkspace />} />
        <Route path="tasks/:id" element={<TaskWorkspace />} />
        {/* Team workspaces share the same underlying component but a distinct
            URL so the sidebar doesn't highlight "Custom Guidance" while
            you're actually inside a Project Team. */}
        <Route path="teams/:id" element={<GuidanceWorkspace />} />
        <Route path="student-upload" element={<StudentProjectUpload />} />
        <Route path="mentors" element={<Mentors />} />
        <Route path="journals" element={<Journals />} />
        <Route path="courses" element={<Courses />} />
        <Route path="courses/:id/edit" element={<CourseEditor />} />
        <Route path="courses/:id/learn" element={<CourseLearn />} />
        <Route path="courses/:id/certificate" element={<Certificate />} />
        <Route path="courses/:id" element={<CourseDetail />} />
        <Route path="surveys" element={<Surveys />} />
        <Route path="surveys/:id/analytics" element={<SurveyAnalytics />} />
        <Route path="portfolio" element={<Portfolio />} />
        <Route path="plagiarism" element={<Plagiarism />} />
      </Route>

      {/* Mentor / Tutor dashboard */}
      <Route path="/mentor" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<MentorDashboard />} />
        <Route path="requests" element={<MentorRequestsPage />} />
        <Route path="projects" element={<MentorProjectsPage />} />
        <Route path="earnings" element={<AuthorEarnings />} />
        <Route path="ideas/:id" element={<IdeaDetail />} />
        <Route path="guidance/:id" element={<GuidanceWorkspace />} />
      </Route>

      {/* Partner Institution */}
      <Route path="/institution" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<InstitutionDashboard />} />
        <Route path="members" element={<InstitutionMembersPage />} />
        <Route path="projects" element={<InstitutionDashboard />} />
      </Route>

      {/* Admin */}
      <Route path="/admin" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="role-approvals" element={<AdminRoleApprovalsPage />} />
        <Route path="pending-ideas" element={<AdminPendingIdeasPage />} />
        <Route path="mentor-requests" element={<AdminMentorRequestsPage />} />
        <Route path="guidance" element={<AdminGuidancePage />} />
        <Route path="vacancies" element={<AdminVacancies />} />
        <Route path="teams" element={<AdminTeams />} />
        <Route path="tasks" element={<AdminTasks />} />
        <Route path="org" element={<AdminOrgUnits />} />
        <Route path="interns" element={<AdminInternAnalytics />} />
        <Route path="payments" element={<AdminPaymentsPage />} />
        <Route path="pricing" element={<AdminPricingPage />} />
        <Route path="tutorials" element={<AdminTutorialsPage />} />
        <Route path="events" element={<AdminEventsPage />} />
        <Route path="course-review" element={<AdminCourseReviewPage />} />
        <Route path="journals" element={<AdminJournalsPage />} />
        <Route path="taxonomy" element={<AdminTaxonomyPage />} />
        <Route path="research-ideas" element={<AdminResearchIdeasPage />} />
        <Route path="datasets" element={<AdminDatasetsPage />} />
        <Route path="explainers" element={<AdminExplainersPage />} />
        <Route path="plans" element={<AdminPricingPlansPage />} />
        <Route path="email-engine" element={<AdminEmailEnginePage />} />
        <Route path="mailbox" element={<AdminMailboxPage />} />
        <Route path="media" element={<AdminMediaManagerPage />} />
        <Route path="cms" element={<AdminCmsPage />} />
        <Route path="leads" element={<AdminLeadsPage />} />
        <Route path="team-roles" element={<AdminTeamRolesPage />} />
        <Route path="blogs" element={<AdminBlogsPage />} />
        <Route path="landing-journey" element={<AdminLandingJourneyPage />} />
        <Route path="papers" element={<AdminPapersPage />} />
        <Route path="projects" element={<AdminProjectsPage />} />
        <Route path="analytics" element={<AdminAnalyticsPage />} />
        <Route path="config" element={<AdminConfigPage />} />
        <Route path="nav-visibility" element={<AdminNavVisibility />} />
        <Route path="ideas/:id" element={<IdeaDetail />} />
      </Route>
    </Routes>
    </>
  );
}

function App() {
  React.useEffect(() => {
    // Bootstrap media-manager overrides so getSlot() returns the latest URLs.
    api.get("/media/public")
      .then((r) => setMediaOverrides(r.data || {}))
      .catch(() => {});
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CallProvider>
            <Preloader />
            <ScrollToTop />
            <Router />
            <PersistentCallHost />
            <Toaster position="top-right" richColors closeButton />
          </CallProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
